/**
 * Note lane feature module (W2-03 note-lane; WORKPACKAGES §W2-03, Appendix B; ARCHITECTURE.md §6.1).
 *
 * Provides: the Chart workspace timeline panel (`chart.timeline`, ChartTimelinePanel: mounts the timeline host with
 * every module's column factories and attaches it to the services) and the tool rail (`chart.tools`); the note lane
 * column type ('lane'); an invisible status bar item (LaneShellHost: the package's dialogs, in every workspace); the
 * marker decorations of the time gutter and overview; the commands `notes.*`,
 * `edit.copy|cut|paste|pasteGhost|pasteImmediate|duplicate|delete|selectAll`, `tools.select|tap|drag|flick|hold|eraser`,
 * `markers.*`, `lane.*`, `time.insert|remove` with their Default and RPE preset bindings; settings rows for the lane's
 * own preferences (incl. "Wheel moves the playhead", W3-05); a help topic; the usability-session tooling of Appendix C
 * H2 (W3-05: `lane.usabilityStart|Stop`, a status bar chip during a session, the `#/dev/usability` task sheet).
 */

import type { FeatureModule, KeyBinding, KeymapPresetId } from '../../app/registry/types';
import { ChartTimelinePanel } from './ChartTimelinePanel';
import { LANE_EDIT_COMMANDS, LANE_EDIT_KEYBINDINGS, MARKERS_ATOM } from './editCommands';
import { laneColumnDef } from './laneColumnDef';
import { LaneShellHost } from './LaneShellHost';
import { m } from './noteLane.messages';
import { markerDecorations, TIME_COMMANDS, TIME_KEYBINDINGS } from './timeCommands';
import { ToolRail } from './ToolRail';
import { USABILITY_COMMANDS, UsabilityChip, UsabilityRoute } from './UsabilityUi';
import { WHEEL_SEEK_EXT } from './wheelSeek';

function bindings(preset: KeymapPresetId): readonly KeyBinding[] {
  return [...LANE_EDIT_KEYBINDINGS[preset], ...TIME_KEYBINDINGS[preset]];
}

const noteLaneModule = {
  id: 'note-lane',
  commands: [...LANE_EDIT_COMMANDS, ...TIME_COMMANDS, ...USABILITY_COMMANDS],
  keybindings: { default: bindings('default'), rpe: bindings('rpe') },
  panels: [
    {
      id: 'chart.timeline',
      title: m.panelTimeline,
      icon: 'AudioLines',
      slot: 'chart.timeline',
      order: 10,
      level: 'guided',
      component: ChartTimelinePanel,
    },
    {
      id: 'chart.tools',
      title: m.panelTools,
      icon: 'MousePointer2',
      slot: 'chart.tools',
      order: 10,
      level: 'guided',
      component: ToolRail,
    },
  ],
  // Mounted by the shell's hint bar in every editor workspace: the time and marker commands also run from the Song &
  // Timing workspace (timing.context, palette), where the Chart timeline panel is not mounted.
  statusItems: [
    { id: 'note-lane.host', slot: 'statusbar', order: 1000, component: LaneShellHost },
    // Facilitator tooling of a scripted usability session (W3-05, Appendix C H2): only while a session runs.
    { id: 'note-lane.usability', slot: 'statusbar', order: 5, component: UsabilityChip },
  ],
  routes: [{ path: '#/dev/usability', component: UsabilityRoute }],
  timelineColumns: (s) => [laneColumnDef(s)],
  timelineDecorations: (s) => markerDecorations(s),
  whenAtoms: (s) => ({ [MARKERS_ATOM]: () => s.editor.getState().selection.markers.size > 0 }),
  settings: [
    {
      path: `ext.${WHEEL_SEEK_EXT}`,
      title: m.settingWheelSeeks,
      description: m.settingWheelSeeksHelp,
      level: 'guided',
      kind: 'enum',
      options: [
        { value: 'preset', label: m.wheelSeeksPreset },
        { value: 'on', label: m.wheelSeeksOn },
        { value: 'off', label: m.wheelSeeksOff },
      ],
    },
    {
      path: 'ext.noteLane.sides',
      title: m.headerSides,
      level: 'full',
      kind: 'enum',
      options: [
        { value: 'both', label: m.sidesBoth },
        { value: 'above', label: m.sidesAbove },
        { value: 'below', label: m.sidesBelow },
      ],
    },
  ],
  helpTopics: [
    {
      id: 'note-lane',
      title: m.laneHelpTitle,
      body: m.laneHelpBody,
      showMe: { panel: 'chart.timeline' },
      level: 'guided',
    },
  ],
} satisfies FeatureModule;

export default noteLaneModule;
